import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import HeroSection from '@/components/HeroSection';
import ArtistCard from '@/components/ArtistCard';
import EventCard from '@/components/EventCard';
import api from '@/lib/api';
import { Sparkles, Mic2, Music2, Camera, Drama, Disc3, User, Brush, Smile, Mic, Headphones, ArrowRight, Star, BadgeCheck, Briefcase, Clock, MapPin, IndianRupee, Film } from 'lucide-react';

const CATEGORIES = [
  {
    name: 'Singer', icon: Mic2, count: '2,840',
    image: 'https://images.unsplash.com/photo-1526218626217-dc65a29bb444?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxzaW5nZXIlMjBuZW9uJTIwc3RhZ2UlMjBjb25jZXJ0fGVufDB8fHx8MTc4MDQyNzIzOXww&ixlib=rb-4.1.0&q=85',
    gradient: 'from-fuchsia-600 via-purple-600 to-purple-900',
    accent: 'text-fuchsia-300',
    span: 'lg:col-span-2 lg:row-span-2',
    aspect: 'aspect-[4/5] lg:aspect-auto',
    blurb: 'Playback · Live · Sufi · Indie',
  },
  {
    name: 'Dancer', icon: Music2, count: '1,920',
    image: 'https://images.pexels.com/photos/4310835/pexels-photo-4310835.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    gradient: 'from-pink-500 via-rose-600 to-fuchsia-900',
    accent: 'text-pink-300', blurb: 'Contemporary · Classical · Hip-hop',
  },
  {
    name: 'DJ', icon: Disc3, count: '860',
    image: 'https://images.unsplash.com/photo-1506157786151-b8491531f063?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxkaiUyMG11c2ljJTIwZmVzdGl2YWwlMjBjcm93ZHxlbnwwfHx8fDE3ODA0MjcyMzl8MA&ixlib=rb-4.1.0&q=85',
    gradient: 'from-cyan-500 via-blue-700 to-indigo-900',
    accent: 'text-cyan-300', blurb: 'EDM · Bollywood · Open-format',
  },
  {
    name: 'Actor', icon: Drama, count: '1,540',
    image: 'https://images.unsplash.com/photo-1587397845856-e6cf49176c70?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxjaW5lbWF0aWMlMjBhY3RvciUyMHBvcnRyYWl0fGVufDB8fHx8MTc4MDQyNzIzOXww&ixlib=rb-4.1.0&q=85',
    gradient: 'from-orange-500 via-red-600 to-rose-900',
    accent: 'text-orange-300', blurb: 'Theatre · OTT · Cinema',
  },
  {
    name: 'Model', icon: User, count: '1,280',
    image: 'https://images.pexels.com/photos/33847264/pexels-photo-33847264.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    gradient: 'from-amber-400 via-orange-500 to-red-700',
    accent: 'text-amber-300', blurb: 'Runway · Editorial · Commercial',
  },
  {
    name: 'Photographer', icon: Camera, count: '1,640',
    image: 'https://images.pexels.com/photos/35057828/pexels-photo-35057828.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    gradient: 'from-emerald-400 via-teal-600 to-cyan-900',
    accent: 'text-emerald-300',
    span: 'col-span-2 lg:col-span-2', aspect: 'aspect-[16/9] lg:aspect-auto',
    blurb: 'Weddings · Editorial · Documentary',
  },
  {
    name: 'Musician', icon: Music2, count: '1,120',
    image: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?crop=entropy&cs=srgb&fm=jpg&w=900&q=80',
    gradient: 'from-violet-500 via-fuchsia-700 to-purple-900',
    accent: 'text-violet-300', blurb: 'Live bands · Studio · Composers',
  },
  {
    name: 'Makeup Artist', icon: Brush, count: '780',
    image: 'https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?crop=entropy&cs=srgb&fm=jpg&w=900&q=80',
    gradient: 'from-rose-400 via-pink-600 to-fuchsia-900',
    accent: 'text-rose-300', blurb: 'Bridal · Editorial · SFX',
  },
  {
    name: 'Comedian', icon: Smile, count: '640',
    image: 'https://images.unsplash.com/photo-1527224857830-43a7acc85260?crop=entropy&cs=srgb&fm=jpg&w=900&q=80',
    gradient: 'from-yellow-400 via-amber-500 to-orange-700',
    accent: 'text-yellow-300', blurb: 'Stand-up · Improv · Roast · Hosts',
  },
  {
    name: 'Influencer', icon: User, count: '1,420',
    image: 'https://images.unsplash.com/photo-1488161628813-04466f872be2?crop=entropy&cs=srgb&fm=jpg&w=900&q=80',
    gradient: 'from-sky-400 via-blue-600 to-indigo-900',
    accent: 'text-sky-300', blurb: 'Reels · Lifestyle · Brand · Travel',
  },
  {
    name: 'Voice Artist', icon: Mic, count: '520',
    image: 'https://images.unsplash.com/photo-1590602847861-f357a9332bbc?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80',
    gradient: 'from-teal-400 via-emerald-600 to-green-900',
    accent: 'text-teal-300',
    span: 'col-span-2 lg:col-span-2', aspect: 'aspect-[16/9] lg:aspect-auto',
    blurb: 'Narration · Dubbing · Animation · Commercials',
  },
];

const Section = ({ eyebrow, title, sub, action, children }) => (
  <section className="max-w-7xl mx-auto px-6 sm:px-12 py-20">
    <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
      <div>
        {eyebrow && <div className="text-xs uppercase tracking-[0.3em] text-fuchsia-300 mb-3">{eyebrow}</div>}
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">{title}</h2>
        {sub && <p className="text-slate-400 mt-3 max-w-2xl">{sub}</p>}
      </div>
      {action}
    </div>
    {children}
  </section>
);

const Home = () => {
  const [featured, setFeatured] = useState([]);
  const [events, setEvents] = useState([]);
  const [castings, setCastings] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/artists/featured').then(({ data }) => setFeatured(data.artists)).catch(() => {});
    api.get('/events').then(({ data }) => setEvents(data.events.slice(0, 3))).catch(() => {});
    api.get('/castings').then(({ data }) => setCastings(data.castings.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div>
      <HeroSection />

      {/* CATEGORIES — cinematic bento */}
      <Section
        eyebrow="Browse"
        title="Every kind of talent, one spectrum"
        sub="From Bollywood playback to underground techno, from contemporary dance to method acting — find the artist that fits your moment."
        action={
          <Link to="/artists" className="text-sm text-slate-300 hover:text-white inline-flex items-center gap-2">
            View all categories <ArrowRight className="h-4 w-4" />
          </Link>
        }
      >
        <div className="grid grid-cols-2 lg:grid-cols-4 lg:grid-rows-4 gap-4 lg:gap-5 lg:auto-rows-[200px]">
          {CATEGORIES.map((c, i) => {
            const Icon = c.icon;
            return (
              <Link
                key={c.name}
                to={`/artists?category=${encodeURIComponent(c.name)}`}
                className={`group relative overflow-hidden rounded-3xl border border-white/10 bg-[#13182C] ${c.aspect || 'aspect-[3/4]'} ${c.span || ''} card-glow noise-overlay`}
                style={{ animationDelay: `${i * 90}ms` }}
              >
                {/* full-bleed image */}
                <img
                  src={c.image}
                  alt={c.name}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
                />

                {/* color tint */}
                <div className={`absolute inset-0 bg-gradient-to-br ${c.gradient} mix-blend-soft-light opacity-80 group-hover:opacity-60 transition-opacity duration-500`} />

                {/* dark gradient for legibility */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#06080F]/95 via-[#06080F]/40 to-transparent" />

                {/* animated rainbow ring on hover */}
                <div className="pointer-events-none absolute -inset-px rounded-3xl opacity-0 group-hover:opacity-100 transition duration-500"
                     style={{ background: 'linear-gradient(135deg, rgba(123,47,247,0.7), rgba(241,7,163,0.7), rgba(6,182,212,0.7))',
                              WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
                              WebkitMaskComposite: 'xor', maskComposite: 'exclude', padding: '1.5px' }} />

                {/* TOP — count chip + icon */}
                <div className="absolute top-4 left-4 right-4 flex items-start justify-between z-10">
                  <div className="glass rounded-full pl-2 pr-3 py-1 flex items-center gap-2">
                    <Icon className={`h-3.5 w-3.5 ${c.accent}`} />
                    <span className="text-[10px] uppercase tracking-[0.2em] text-white font-medium">{c.count} artists</span>
                  </div>
                  <div className="h-9 w-9 rounded-xl glass-strong border border-white/15 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 group-hover:rotate-[-15deg] transition-all duration-500">
                    <ArrowRight className="h-4 w-4" />
                  </div>
                </div>

                {/* BOTTOM — name + blurb */}
                <div className="absolute inset-x-0 bottom-0 p-5 lg:p-6 z-10">
                  <div className={`text-[10px] uppercase tracking-[0.3em] ${c.accent} mb-2 opacity-90`}>{c.blurb}</div>
                  <div className="font-display font-black tracking-tight text-white leading-none drop-shadow-lg text-[clamp(1.75rem,3.5vw,3.5rem)]">
                    {c.name}
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="inline-flex items-center gap-2 text-sm text-white/80 group-hover:text-white transition">
                      <span className="relative">
                        Explore
                        <span className="absolute left-0 -bottom-0.5 h-px w-0 bg-white group-hover:w-full transition-all duration-500" />
                      </span>
                      <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
                    </div>
                    {/* tiny equalizer accent for hero tile */}
                    {c.span?.includes('row-span-2') && (
                      <div className="hidden lg:flex items-end gap-0.5 h-4">
                        {[6, 12, 8, 16, 10, 14].map((h, idx) => (
                          <span key={`tile-eq-${idx}-${h}`} className="w-0.5 bg-white/70 rounded-full"
                                style={{ height: `${h}px`, animation: `eq-bounce ${0.7 + (idx % 4) * 0.15}s ease-in-out ${idx * 0.12}s infinite alternate` }} />
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* spotlight glow at bottom */}
                <div className={`pointer-events-none absolute -bottom-10 left-1/2 -translate-x-1/2 h-24 w-3/4 rounded-full bg-gradient-to-r ${c.gradient} opacity-50 blur-3xl group-hover:opacity-80 transition`} />
              </Link>
            );
          })}
        </div>
      </Section>

      {/* SPACER between categories and featured */}
      <div className="h-16 sm:h-24 lg:h-32" aria-hidden />

      {/* FEATURED ARTISTS */}
      <Section
        eyebrow="Spotlight"
        title="Featured artists this week"
        sub="Hand-picked verified talent across India and beyond, ready to collaborate."
        action={
          <Link to="/artists" className="text-sm text-slate-300 hover:text-white inline-flex items-center gap-2">
            View all <ArrowRight className="h-4 w-4" />
          </Link>
        }
      >
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {featured.map((a) => <ArtistCard key={a.artist_id} artist={a} />)}
        </div>
      </Section>

      {/* CASTINGS — cinematic spotlight strip */}
      <section className="relative my-12 lg:my-20 overflow-hidden">
        {/* full-bleed cinematic background */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B1020] via-[#1a0a14] to-[#0B1020]" />
          <div className="absolute -top-20 left-1/4 h-96 w-96 rounded-full bg-rose-600/25 blur-[120px]" />
          <div className="absolute -bottom-20 right-1/4 h-96 w-96 rounded-full bg-orange-500/20 blur-[120px]" />
          {/* film grain */}
          <div className="absolute inset-0 opacity-20"
               style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)', backgroundSize: '36px 36px' }} />
          {/* horizontal scan lines */}
          <div className="absolute inset-0 opacity-[0.08]"
               style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(255,255,255,0.4) 3px, rgba(255,255,255,0.4) 4px)' }} />
        </div>

        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-20 lg:py-28">
          {/* Header row */}
          <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8 mb-12">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 mb-5 text-[10px] uppercase tracking-[0.3em] border border-rose-400/30 bg-rose-500/10 text-rose-200">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-rose-400" />
                </span>
                Now casting · {castings.length} open calls
              </div>
              <h2 className="font-display font-black tracking-[-0.03em] leading-[0.95] text-white text-[clamp(2.5rem,6vw,5rem)]">
                The next big <span className="italic" style={{ background: 'linear-gradient(135deg,#FACC15,#F97316,#F107A3)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>break</span>
                <br />could be yours.
              </h2>
              <p className="text-slate-300 mt-5 leading-relaxed max-w-xl">
                Live auditions from streaming platforms, ad agencies, music labels and brands.
                One profile · one tap to apply · zero gatekeepers.
              </p>
              <div className="flex flex-wrap gap-3 mt-7">
                <Link to="/castings" className="btn-rainbow rounded-full px-6 py-3 text-white font-medium inline-flex items-center gap-2 text-sm">
                  <Film className="h-4 w-4" /> See all auditions
                </Link>
                <Link to="/post/casting" className="glass-strong rounded-full px-6 py-3 text-white font-medium border border-white/15 hover:border-amber-400/40 transition text-sm inline-flex items-center gap-2">
                  <Briefcase className="h-4 w-4" /> Post a casting
                </Link>
              </div>
            </div>

            {/* big number stat */}
            <div className="hidden lg:block text-right">
              <div className="font-display font-black tracking-tighter text-transparent text-[8rem] leading-none"
                   style={{ WebkitTextStroke: '1.5px rgba(241,7,163,0.5)' }}>
                24
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-rose-300 -mt-2">new this week</div>
            </div>
          </div>

          {/* Cinematic cards row */}
          <div className="grid md:grid-cols-3 gap-5">
            {castings.map((c, i) => (
              <Link
                key={c.casting_id}
                to={`/castings/${c.casting_id}`}
                className="group relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#1a0e18] to-[#0B1020] p-7 card-glow flex flex-col"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                {/* clapboard / film strip texture left edge */}
                <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-amber-400 via-rose-500 to-fuchsia-600" />
                <div className="absolute left-2 top-0 bottom-0 w-1 flex flex-col justify-around py-3 opacity-30">
                  {Array.from({ length: 12 }).map((_, k) => <span key={`strip-${k}`} className="h-1 w-1 rounded-sm bg-white" />)}
                </div>

                {/* Role badge + animated number */}
                <div className="flex items-start justify-between mb-5 pl-3">
                  <div className="flex items-center gap-2">
                    <Film className="h-4 w-4 text-amber-300" />
                    <span className="text-[10px] uppercase tracking-[0.25em] text-amber-200/90">{c.role_type}</span>
                  </div>
                  <span className="font-display font-black text-3xl text-transparent" style={{ WebkitTextStroke: '1px rgba(255,255,255,0.25)' }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                </div>

                {/* Title */}
                <h3 className="font-display text-2xl text-white font-bold leading-tight mb-3 pl-3 group-hover:text-amber-200 transition">
                  {c.title}
                </h3>
                <p className="text-sm text-slate-400 line-clamp-2 mb-6 pl-3">{c.description}</p>

                {/* Stats grid with icons */}
                <div className="pl-3 mt-auto space-y-2.5 pt-5 border-t border-white/5">
                  <div className="flex items-center gap-3 text-xs text-slate-300">
                    <MapPin className="h-3.5 w-3.5 text-cyan-300 shrink-0" /> {c.location}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-300">
                    <Clock className="h-3.5 w-3.5 text-rose-300 shrink-0" /> Apply by {c.deadline}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-300">
                    <IndianRupee className="h-3.5 w-3.5 text-amber-300 shrink-0" /> {c.budget || 'Negotiable'}
                  </div>
                </div>

                {/* Apply CTA */}
                <div className="pl-3 mt-6 inline-flex items-center gap-2 text-sm text-white">
                  <span className="relative">
                    Apply now
                    <span className="absolute left-0 -bottom-0.5 h-px w-0 bg-amber-300 group-hover:w-full transition-all duration-500" />
                  </span>
                  <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1 text-amber-300" />
                </div>

                {/* Spotlight hover glow */}
                <div className="pointer-events-none absolute -bottom-16 left-1/2 -translate-x-1/2 h-32 w-3/4 rounded-full bg-gradient-to-r from-amber-500 via-rose-500 to-fuchsia-600 opacity-0 group-hover:opacity-40 blur-3xl transition duration-500" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-cyan-300 mb-3">For Artists</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-5">
              Your AI <span className="ai-text">Talent Manager</span>, always on
            </h2>
            <p className="text-slate-400 mb-8 leading-relaxed">
              Paste your social links. Our AI generates a polished bio, suggests skills, and curates your portfolio in seconds.
              Then it helps you respond to inquiries, quote prices and grow your audience.
            </p>
            <ul className="space-y-3 mb-10">
              {[
                'Auto-generated cinematic bio',
                'AI-curated portfolio from your social links',
                'Smart pricing & response suggestions',
                'Verification & trust score',
              ].map((t) => (
                <li key={t} className="flex items-center gap-3 text-slate-200">
                  <BadgeCheck className="h-5 w-5 text-emerald-400" /> {t}
                </li>
              ))}
            </ul>
            <Link to="/onboarding" className="btn-ai rounded-full px-7 py-3 text-white font-medium inline-flex items-center gap-2">
              <Sparkles className="h-4 w-4" /> Build your AI portfolio
            </Link>
          </div>
          <div className="relative">
            <div className="absolute -inset-8 bg-gradient-to-br from-fuchsia-500/20 via-cyan-500/10 to-amber-500/20 blur-3xl rounded-full" />
            <div className="relative rounded-3xl overflow-hidden border border-white/10 glass-strong">
              <img
                src="https://images.unsplash.com/photo-1526218626217-dc65a29bb444?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxzaW5nZXIlMjBuZW9uJTIwc3RhZ2UlMjBjb25jZXJ0fGVufDB8fHx8MTc4MDQyNzIzOXww&ixlib=rb-4.1.0&q=85"
                alt="Artist"
                className="w-full aspect-[4/5] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* EVENTS */}
      <Section
        eyebrow="What's on"
        title="Upcoming events"
        sub="Live shows, festivals, workshops and fan meetups across the country."
        action={
          <Link to="/events" className="text-sm text-slate-300 hover:text-white inline-flex items-center gap-2">
            All events <ArrowRight className="h-4 w-4" />
          </Link>
        }
      >
        <div className="grid lg:grid-cols-2 gap-5">
          {events.map((e) => <EventCard key={e.event_id} event={e} />)}
        </div>
      </Section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 sm:px-12 py-24">
        <div className="relative rounded-3xl overflow-hidden glass-strong border border-white/10 px-10 py-16 text-center noise-overlay">
          <div className="absolute -top-20 -left-20 h-64 w-64 rounded-full bg-fuchsia-500/30 blur-3xl" />
          <div className="absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-cyan-500/30 blur-3xl" />
          <Star className="h-10 w-10 text-amber-300 mx-auto mb-4" />
          <h2 className="font-display text-3xl sm:text-5xl font-bold text-white tracking-tight mb-4">
            Ready to find your <span className="rainbow-text">spectrum</span>?
          </h2>
          <p className="text-slate-300 max-w-xl mx-auto mb-8">
            Join thousands of artists and creators turning Maarivil into the future of entertainment discovery.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link to="/register" className="btn-rainbow rounded-full px-7 py-3 text-white font-medium">Get started — it's free</Link>
            <Link to="/artists" className="text-slate-200 hover:text-white text-sm">or explore the universe →</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
